// Turns the list of saved games into the records screen: best games, the
// teams that scored best together, and who you've played with.
//
// A player in a saved game is { uid, pid, name, colour, lines, pieces }:
// `uid` is the signed-in account on that player's computer and `pid` which
// of that account's couch players it was (both null for a guest). The
// pair is the player's identity - names can change, so the newest name
// seen for an identity is the one shown.

import { groupBonus, SCORING_VERSION } from '../engine/game.js';

export const playerKey = (p) => (p.uid && p.pid ? `${p.uid}:${p.pid}` : null);

function latestNames(games) {
  const names = {};
  [...games].sort((a, b) => (a.at || 0) - (b.at || 0)).forEach((g) => {
    g.players.forEach((p) => { const k = playerKey(p); if (k) names[k] = p.name; });
  });
  return names;
}

// A game's score on today's scale. Games saved before the group bonus
// (no `scoring`) get the whole score multiplied by it - close enough, as
// line points are nearly all of a score.
export function scoreOf(g) {
  if ((g.scoring || 1) >= SCORING_VERSION) return g.score;
  return Math.round(g.score * groupBonus(g.players.length));
}

export function computeStats(games, myUid) {
  const list = (games || []).filter((g) => g && Array.isArray(g.players))
    .map((g) => ({ ...g, score: scoreOf(g) }));
  const names = latestNames(list);
  const nameOf = (p) => names[playerKey(p)] || p.name || 'Guest';

  // Every game, highest first - the records screen shows the top ten and
  // the rest on "Show all".
  const best = [...list]
    .sort((a, b) => b.score - a.score)
    .map((g) => ({
      id: g.id, score: g.score, lines: g.lines, at: g.at, players: g.players.map(nameOf),
    }));

  // Teams: everyone in the game. Guests count as "Guest" - two guests in a
  // game make a team of "Guest + Guest + ...", which is still worth seeing.
  const teams = {};
  list.forEach((g) => {
    if (g.players.length < 2) return;
    const members = g.players.map((p) => ({ key: playerKey(p) || `guest:${p.name}`, p }))
      .sort((a, b) => a.key.localeCompare(b.key));
    const key = members.map((m) => m.key).join('|');
    const t = teams[key] || (teams[key] = {
      key, names: members.map((m) => (playerKey(m.p) ? nameOf(m.p) : 'Guest')), games: 0, best: 0, total: 0, lines: 0,
    });
    t.games += 1;
    t.best = Math.max(t.best, g.score);
    t.total += g.score;
    t.lines += g.lines;
  });
  const teamList = Object.values(teams)
    .map((t) => ({ ...t, average: Math.round(t.total / t.games) }))
    .sort((a, b) => b.best - a.best);

  // Played with: everyone signed in who wasn't one of mine.
  const others = {};
  list.forEach((g) => {
    g.players.forEach((p) => {
      const k = playerKey(p);
      if (!k || p.uid === myUid) return;
      const o = others[k] || (others[k] = { key: k, name: nameOf(p), games: 0, last: 0 });
      o.games += 1;
      o.last = Math.max(o.last, g.at || 0);
    });
  });
  const playedWith = Object.values(others).sort((a, b) => b.games - a.games || b.last - a.last);

  // My own couch players.
  const mine = {};
  list.forEach((g) => {
    g.players.forEach((p) => {
      const k = playerKey(p);
      if (!k || p.uid !== myUid) return;
      const m = mine[k] || (mine[k] = {
        key: k, pid: p.pid, name: nameOf(p), games: 0, best: 0, lines: 0, cleared: 0,
      });
      m.games += 1;
      m.best = Math.max(m.best, g.score);
      m.lines += p.lines || 0;
      m.cleared += p.cleared || 0;
    });
  });

  return {
    games: list.length, best, teams: teamList, playedWith, mine: Object.values(mine).sort((a, b) => b.games - a.games),
  };
}
